module Client.Summernote

open Fable.Core
open Fable.Core.JsInterop
open Fable.SimpleHttp
open Fable.React
open System.Text.RegularExpressions
open Browser.Types

importAll "summernote/dist/summernote-lite.css"
importAll "summernote/dist/summernote-lite"

let private jquery = importAll<obj> "jquery"

/// Assorted buttons and dropdowns that can be included in the Summernote toolbar
[<StringEnum>]
type SummernoteButton =
    | Picture
    | Link
    | Video
    | Table
    | Hr
    | Fontname
    | Fontsize
    | Fontsizeunit
    | Color
    | Forecolor
    | Backcolor
    | Bold
    | Italic
    | Underline
    | Strikethrough
    | Superscript
    | Subscript
    | Clear
    | Style
    | Ol
    | Ul
    | Paragraph
    | Height
    | Fullscreen
    | Codeview
    | Undo
    | Redo
    | Help

/// The content of the editor, as HTML markup
type EditorContentHtml = string
/// The element affected by the editor change (ie. the underlying element)
type AffectedElement = ReactElement

/// Callbacks exposed by the editor
type SummernoteCallbacks =
    {
        /// <summary>onChange is called every time the content of the editor changes</summary>
        /// <param name="content">New content</param>
        /// <param name="element">HTML element affected</param>
        onChange: EditorContentHtml -> AffectedElement -> unit

        /// <summary>onImageUpload is called every time an image is uploaded</summary>
        /// <param name="files">A JS FileList object containing all of the uploaded files</param>
        /// <remarks>See https://developer.mozilla.org/en-US/docs/Web/API/FileList for more information</remarks>
        onImageUpload: Browser.Types.FileList -> unit

        /// <summary>onFocus is called every time the editor window is focussed</summary>
        onFocus: unit -> unit

        /// <summary>onBlur is called every time the editor window loses focus</summary>
        onBlur: unit -> unit

        /// <summary>onPaste is called every time something is pasted into the editor window</summary>
        onPaste: ClipboardEvent -> unit
    }

module SummernoteCallbacks =
    let none =
        {
            onChange = fun _ -> ignore
            onImageUpload = ignore
            onFocus = ignore
            onBlur = ignore
            onPaste = ignore
        }

/// An array of tuples where the first value is the name of the category, and the second is
/// a list of toolbar buttons to include in the category
type SummernoteToolbar = (string * SummernoteButton array) array

module SummernoteToolbar =
    let defaults =
        [|
            ("pre-defined-formats", [| SummernoteButton.Style |])
            ("controls", [| SummernoteButton.Undo; SummernoteButton.Redo |])
            ("font", [| SummernoteButton.Fontname; SummernoteButton.Fontsize |])
            ("style", [| SummernoteButton.Bold; SummernoteButton.Italic; SummernoteButton.Underline; (* SummernoteButton.Strikethrough; SummernoteButton.Subscript; SummernoteButton.Superscript; *) SummernoteButton.Clear |])
            ("colour", [| SummernoteButton.Forecolor; SummernoteButton.Backcolor |])
            ("paragraph", [| SummernoteButton.Ul; SummernoteButton.Ol; SummernoteButton.Paragraph |])
            ("table", [| SummernoteButton.Table |])
            ("media", [| SummernoteButton.Link; SummernoteButton.Hr |])
            // ("help", [| SummernoteButton.Help |])
        |]

/// An individual style in the pre-defined format list
type SummernoteStyle =
    {
        /// The HTML tag to render the item in the dropdown with
        tag: string
        /// The title of the item in the dropdown
        title: string
        /// The inline CSS styling to apply to the item in the dropdown
        style: string
        /// The class to apply to both the item in the dropdown, and any formatted text
        className: string
        /// The value to apply to the text when clicked
        value: string
    }

module SummernoteStyle =
    let defaults =
        [|
            { tag = "p"; title = "Heading 1"; style = "font-size: 32px;"; className = ""; value = "h1" }
            { tag = "p"; title = "Heading 2"; style = "font-size: 28px;"; className = ""; value = "h2" }
            { tag = "p"; title = "Heading 3"; style = "font-size: 22px; font-weight: bold;"; className = ""; value = "h3" }
            { tag = "p"; title = "Heading 4"; style = "font-size: 18px;"; className = ""; value = "h4" }
            { tag = "p"; title = "Normal"; style = "font-size: 14px;"; className = ""; value = "p" }
            { tag = "p"; title = "Block Quote"; style = ""; className = ""; value = "blockquote" }
        |]

type SummernoteParams =
    {
        /// The initial height of the editor in px
        height: int
        /// The minimum height of the editor in px, should the user resize it
        minHeight: int
        /// The maximum height of the editor in px, should the user resize it
        maxHeight: int
        /// True to show overlays in the body of the document, false to show them as a child of the editor.
        /// This must be true in our systems as overlays in the editor don't display.
        dialogsInBody: bool
        /// True to inherit the placeholder from the element that was turned into the editor.
        /// Should usually be true.
        inheritPlaceholder: bool
        /// True to disable tab indentation (ie. so you can use tab for form navigation instead)
        tabDisable: bool
        /// A list of font names to include in the font selector
        fontNames: string array
        /// True to include Arial and Arial Black by default
        addDefaultFonts: bool
        toolbar: SummernoteToolbar
        styleTags: SummernoteStyle array
        callbacks: SummernoteCallbacks
    }

module SummernoteParams =
    let defaults =
        {
            height = 300
            minHeight = 300
            maxHeight = 600
            dialogsInBody = true
            inheritPlaceholder = true
            tabDisable = false
            fontNames = [|
                // Copied from TinyMCE
                // "Andale Mono"
                "Arial"
                "Arial Black"
                // "Book Antiqua"
                "Comic Sans MS"
                "Courier New"
                // "Georgia"
                "Helvetica"
                // "Impact"
                // "Tahoma"
                "Times New Roman"
                // "Trebuchet MS"
                // "Verdana"
            |]
            addDefaultFonts = false
            toolbar = SummernoteToolbar.defaults
            styleTags = SummernoteStyle.defaults
            callbacks = SummernoteCallbacks.none
        }

type ISummernote =
    [<Emit("$0.summernote($1)")>]
    member _.init (_params: SummernoteParams) = jsNative

    [<Emit("$0.summernote(\"code\")")>]
    member _.getCode () = jsNative

    [<Emit("$0.summernote(\"code\", $1)")>]
    member _.setCode (_code: string) = jsNative

    [<Emit("$0.summernote(\"insertText\", $1)")>]
    member _.insertText (_text: string) = jsNative

    [<Emit("$0.summernote(\"insertImage\", $1)")>]
    member _.insertImage (_imageURL: string) = jsNative

    [<Emit("$0.summernote(\"editor.saveRange\")")>]
    member _.saveRange () = jsNative

    [<Emit("$0.summernote(\"editor.focus\")")>]
    member _.focus () = jsNative

    [<Emit("$0.summernote(\"editor.restoreRange\")")>]
    member _.restoreRange () = jsNative

    member self.insertTextAtCurrentCursor (_text: string) =
        // insertText inserts on the last range (ie. the last cursor position or highlighted text)
        // Unfortunately the range logic seems to go awry when the editor loses focus, hence
        // why we have to restore the range to get the intended behaviour.
        // It's *quirky* - https://github.com/summernote/summernote/blob/main/src/js/module/Editor.js#L136
        self.saveRange()
        self.restoreRange()
        self.insertText _text

type SummernoteProps =
    {
        /// The Id of the editor
        Id: string
        Props: SummernoteParams
        /// Called whenever anything in the editor changes, providing the new content
        OnChange: string -> unit
        /// Called whenever a file is uploaded, providing the editor and file.
        /// It's recommended that you use the defaultImageUploadHandler
        OnUploadFile: ISummernote -> Browser.Types.File -> unit
        /// The element hosting the editor
        Host: ReactElement
        Template: string option
    }

type SummernoteState =
    {
        mutable Contents: string
    }

/// <summary>Get an instance of a Summernote editor by Id</summary>
/// <param name="id">The HTML Id of the editor</param>
let get id = (jquery $ $"#%s{id}") :> ISummernote

/// <summary>
/// An image upload handler that uploads the given image to the specified URL, expecting the URL to
/// return the location of the image, then inserts the image in the editor if successful
/// </summary>
/// <param name="url">The URL to upload the image to</param>
/// <param name="failureHandler">A callback used if anything goes wrong during the upload, providing an error message</param>
/// <param name="editor">The instance of the Summernote editor</param>
/// <param name="file">The file to upload</file>
let defaultImageUploadHandler (url: string) (getToken: unit -> string option) (failureHandler: string -> unit) (editor: ISummernote) (file: Browser.Types.File) =

    // Null check required as the drag-and-drop file upload doesn't seem to be debounced, so sometimes it tries to upload
    // a file after it's already been uploaded (and said extra file is null)
    if file <> null then
        match getToken() with
        | None -> failureHandler "Could not get authorisation token"
        | Some token ->
            let fData = FormData.create()
            fData.append("file", file)
    
            url
            |> Http.request 
            |> Http.method POST
            |> Http.content (BodyContent.Form fData)
            |> Http.header (Headers.authorization $"bearer %s{token}")
            |> Http.send
            |> Async.StartAsPromise
            |> Promise.iter (fun response ->
                match response.statusCode with
                | 200 -> editor.insertImage response.responseText
                | 403 -> failureHandler "You do not have permission to perform this action"
                | sc -> failureHandler $"Failed to upload image (status code %i{sc})"
            )

module private Regex =
    // Remove Base64-encoded images
    let [<Literal>] BASE_64_IMG = """<img width="[0-9]+" height="[0-9]+" src="data:.*base64,(.*)">"""

    // Remove XML comments
    let [<Literal>] XML_COMMENTS = """<!--(?s:.*?)-->"""

    // Remove all properties from tables and table rows
    let [<Literal>] TABLE_FORMATTING = """<(table|tr)\s.*?>"""

    // Remove nonsense CSS properties from MS Office products
    let [<Literal>] MS_OFFICE_STYLING = """mso-[\w+-]+:[^;']+;?"""

    // Remove classes from MS Office products
    let [<Literal>] MS_OFFICE_CLASSES = """class=Mso\w+\s?"""

    // HACK - Used to remove CSS properties with negative values
    let [<Literal>] NEGATIVE_OFFSETS = """-\d+(?:\.\d+)?(?=[^<]*>)"""

/// If a change means that the new editor content is this value in length
/// or greater than that of the old content, we count it as a paste
let [<Literal>] EDIT_IS_PASTE_CHARACTER_THRESHOLD = 50

let private regexReplace (pattern: string) (replacement: string) (input: string) =
    Regex.Replace(input, pattern, replacement, RegexOptions.Multiline)

let private regexRemove (pattern: string) = regexReplace pattern ""

let sanitiseEditorContent =
    regexRemove Regex.BASE_64_IMG
    >> regexRemove Regex.XML_COMMENTS
    >> regexReplace Regex.TABLE_FORMATTING "<$1>"
    >> regexReplace Regex.NEGATIVE_OFFSETS "0"
    >> regexRemove Regex.MS_OFFICE_CLASSES
    // HACK - Fix this, Office junk usually represents > 33% of the total size
    // >> regexRemove Regex.MS_OFFICE_STYLING

type SummernoteComponent(props) =
    inherit PureComponent<SummernoteProps, SummernoteState>(props)

    do
        base.setInitState({ Contents = "" })

    member this.get () =
        get this.props.Id
        
    override this.componentDidUpdate (oldProps, state) = 
        let editor = this.get()
        let content = editor.getCode()
        if oldProps.Template <> this.props.Template && this.props.Template <> Some content && this.props.Template <> Some state.Contents then
            match this.props.Template with
            | Some t ->
                this.setState (fun state props -> { state with Contents = props.Template |> Option.defaultValue "" })
                editor.setCode t
            | None ->
                editor.setCode ""

    override this.render () =
        this.props.Host

    override this.componentDidMount (): unit =
        let editor = this.get()
        editor.init
            { SummernoteParams.defaults with
                callbacks = {
                    onChange = fun contents _ ->
                        let oldLength = String.length this.state.Contents
                        let newLength = String.length contents

                        let contents =
                            if newLength > oldLength + EDIT_IS_PASTE_CHARACTER_THRESHOLD then
                                sanitiseEditorContent contents
                            else
                                contents

                        this.state.Contents <- contents
                        this.props.OnChange contents
                    onImageUpload = fun files ->
                        seq { 0..files.length }
                        |> Seq.map files.item
                        |> Seq.iter (this.props.OnUploadFile editor)
                    onBlur = ignore
                    onFocus = ignore
                    onPaste = ignore 
                }
            }

        editor.setCode (this.props.Template |> Option.defaultValue "")

let inline richText props = ofType<SummernoteComponent,_,_> props []
 